import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CoreConstants } from 'merlin-core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TransmissionDetailService } from 'projects/clinician/src/app/patient-list/services/transmission-detail.service';

@Injectable({
    providedIn: 'root'
})
export class EmailLinkService {
    constructor(
        private router: Router,
        private transmissionDetailService: TransmissionDetailService
    ) {}

    handleEmailLink(customerAccountLocationIdList?: number[]): Observable<boolean> {
        const websiteNotificationId = window.sessionStorage.getItem('emailWebsiteNotificationId');
        const patientId = window.sessionStorage.getItem('emailPatientId');

        if (!websiteNotificationId || !patientId) {
            return of(false);
        }

        const locationId = window.sessionStorage.getItem('emailLocationId');
        if (customerAccountLocationIdList !== undefined) {
            // SP2 Physician: locationId is mandatory and must be in the allowed list
            const isAllowed = !!locationId &&
                Array.isArray(customerAccountLocationIdList) &&
                customerAccountLocationIdList.includes(+locationId);
            if (!isAllowed) {
                this.clearEmailLinkStorage(true);
                return of(false);
            }
        }

        const reportTypeCode = window.sessionStorage.getItem('emailReportTypeCode');
        this.transmissionDetailService.setWebsiteNotificationId(+websiteNotificationId);
        this.transmissionDetailService.setPatientId(+patientId);

        return this.transmissionDetailService
            .fetchTransmissionDetail(+websiteNotificationId, +patientId)
            .pipe(
                map((detail: any) => {
                    this.clearEmailLinkStorage(false);
                    this.transmissionDetailService.setTransmissionDetail(detail);
                    this.navigateToTransmission(websiteNotificationId, patientId, reportTypeCode, detail);
                    return true;
                }),
                catchError(() => {
                    this.clearEmailLinkStorage(true);
                    return of(false);
                })
            );
    }

    private navigateToTransmission(websiteNotificationId: string, patientId: string, reportTypeCode: string, leanResponse: any): void {
        // CoreConstants.reportTypeList values are full sub-paths, e.g. 'summary/pacingSummary'.
        const reportTypeList: Record<string, string> = CoreConstants.reportTypeList as Record<string, string>;

        // rightNavMenuItems is nested: parent groups contain subTabMenuItems with the actual report typeCodes.
        const getFirstLeafTypeCode = (): string | null => {
            const groups: Array<any> = leanResponse?.menu?.rightNavMenuItems || [];
            for (const group of groups) {
                const subItems: Array<any> = group.subTabMenuItems || [];
                if (subItems.length > 0) {
                    const first = subItems.find((s: any) => reportTypeList[s.typeCode]);
                    if (first) { return first.typeCode; }
                } else if (reportTypeList[group.typeCode]) {
                    return group.typeCode;
                }
            }
            return null;
        };

        // Determine the effective report type code — prefer explicit NTFYID value, fall back to menu.
        let effectiveReportTypeCode: string = (reportTypeCode && reportTypeList[reportTypeCode])
            ? reportTypeCode
            : getFirstLeafTypeCode();

        let routePath: string;
        if (effectiveReportTypeCode && reportTypeList[effectiveReportTypeCode]) {
            routePath = reportTypeList[effectiveReportTypeCode];
        } else {
            routePath = 'summary/fastPathSummary';
        }
        const queryParams: Record<string, string> = { websiteNotificationId, patientId };
        if (effectiveReportTypeCode) {
            queryParams['reportTypeCode'] = effectiveReportTypeCode;
        }
        this.router.navigate(
            [`/patient-list/transmission/${routePath}`],
            { queryParams }
        );
    }

    private clearEmailLinkStorage(invalid: boolean): void {
        window.sessionStorage.removeItem('emailWebsiteNotificationId');
        window.sessionStorage.removeItem('emailPatientId');
        window.sessionStorage.removeItem('emailReportTypeCode');
        window.sessionStorage.removeItem('emailLocationId');
        if (invalid) {
            window.sessionStorage.setItem('emailLinkInvalid', 'true');
        } else {
            window.sessionStorage.removeItem('emailLinkInvalid');
        }
    }
}
